(() => {
  if (!customElements.get("request-quote")) {
    /**
     * @class RequestQuote
     * @extends HTMLElement
     *
     * @classdesc Custom HTML element for handling request quote functionality.
     *
     * @property {HTMLFormElement} form - The form element within the component.
     * @property {HTMLElement} productImageEl - The element displaying the product image.
     * @property {HTMLElement} productTitleEl - The element displaying the product title.
     * @property {HTMLElement} productSku - The element displaying the product SKU.
     * @property {HTMLInputElement} productInfoInput - The input element for product information.
     * @property {HTMLElement} closeBtn - The button element to close the popup.
     * @property {HTMLElement} innerEl - The inner element of the popup.
     * @property {Object} product - The product data.
     * @property {Object} variant - The variant data.
     *
     * @method setProperties - Initializes properties by querying DOM elements.
     * @method setEventListeners - Sets up event listeners for various interactions.
     * @method onDocumentClick - Handles click events on the document to trigger the quote request.
     * @method checkUrlHash - Checks the URL hash to open the popup if necessary.
     * @method getPersistedData - Retrieves persisted data from localStorage.
     * @method setPersistedData - Persists data to localStorage.
     * @method renderPopup - Renders the popup with product and variant information.
     * @method onInnerClick - Prevents event propagation within the inner element.
     * @method open - Opens the popup and updates the URL hash.
     * @method close - Closes the popup, resets the form, and clears messages.
     */
    class RequestQuote extends HTMLElement {
      constructor() {
        super();

        this.setProperties();
        this.setEventListeners();
        this.getPersistedData();
        this.renderPopup();
        this.checkUrlHash();
      }

      setProperties() {
        this.form = this.querySelector("form");
        this.productImageEl = this.querySelector("[data-product-image]");
        this.productTitleEl = this.querySelector("[data-product-title]");
        this.productSku = this.querySelector("[data-product-sku]");

        this.productInfoInput = this.querySelector("[data-product-info]");

        this.closeBtn = this.querySelector("[data-close]");
        this.innerEl = this.querySelector("[data-inner]");
      }

      setEventListeners() {
        document.addEventListener("click", this.onDocumentClick.bind(this));
        window.addEventListener("hashchange", this.checkUrlHash.bind(this));

        this.addEventListener("click", this.close.bind(this));
        this.innerEl.addEventListener("click", this.onInnerClick.bind(this));
        this.closeBtn.addEventListener("click", this.close.bind(this));
      }

      onDocumentClick(event) {
        if (!event.target.closest("[data-request-quote-trigger]")) return;
        event.preventDefault();

        const triggerEl = event.target.closest("[data-request-quote-trigger]");
        const variantJson = triggerEl.dataset.requestQuoteTrigger;
        if (!variantJson) return;

        const productJson = triggerEl.dataset.productJson;
        if (!productJson) return;

        this.product = JSON.parse(productJson);
        this.variant = JSON.parse(variantJson);

        this.setPersistedData();

        this.renderPopup();
        this.open();
      }

      checkUrlHash() {
        const hash = window.location.hash;
        if (hash !== "#contact_form") return;

        this.open();
      }

      getPersistedData() {
        const persistedData = localStorage.getItem("request-quote");
        if (!persistedData) return;

        const { product, variant } = JSON.parse(persistedData);
        this.product = product;
        this.variant = variant;
      }

      setPersistedData() {
        localStorage.setItem("request-quote", JSON.stringify({ product: this.product, variant: this.variant }));
      }

      renderPopup() {
        if (!this.product || !this.variant) return;

        this.productImageEl.src = this.variant.featured_image || this.product.featured_image || "";
        this.productTitleEl.textContent = this.variant.name;
        this.productSku.textContent = this.variant.sku || "";

        const contactBody = `${this.variant.name} / ${this.variant.sku}`;
        this.productInfoInput.value = contactBody;
      }

      onInnerClick(event) {
        event.stopPropagation();
      }

      open() {
        const url = new URL(window.location.href);
        url.hash = "contact_form";
        window.history.replaceState(null, null, url.toString());

        this.classList.add("open");
        document.body.classList.add("overflow-hidden");
      }

      close() {
        const url = new URL(window.location.href);
        url.hash = "";
        window.history.replaceState(null, null, url.toString());

        this.classList.remove("open");
        document.body.classList.remove("overflow-hidden");

        this.form.reset();
        this.querySelectorAll("[data-message]").forEach((el) => el.remove());
      }
    }
    customElements.define("request-quote", RequestQuote);
  }
})();
